import { Controller, useForm } from 'react-hook-form'
import { Dialog } from 'components/dialogs/Dialog'
import { BreakoutTextInput } from 'components/design-system/BreakoutTextInput'
import { Trans } from 'react-i18next'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { formatInt } from 'util/number-input'
import { useDialogs } from 'hooks/dialogs'
import {
  formatCurrency,
  noTryAsync,
} from '@breakoutlearning/firebase-repository/util'
import {
  calculateInvoiceRequestEstimatedCost,
  isInvoiceRequestProductPriced,
} from '@breakoutlearning/firebase-repository/sources-of-truth/invoiceRequestEstimatedCost'
import { useRepository } from 'hooks/auth'
import { toast } from 'react-hot-toast'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { useMemo, useId, useState, useEffect } from 'react'
import classNames from 'classnames'
import { type Section } from '@breakoutlearning/firebase-repository/models/Section'
import { observer } from 'mobx-react-lite'
import { useTranslationTyped } from 'i18n/i18n'

type FormValues = {
  sectionRequestAssignmentCount: string
  sectionRequestStudentCount: string
}

export const InvoiceRequestDialog = observer(function InvoiceRequestDialog({
  section,
  createSectionInvoiceRequest,
  onCreate,
  onSkip,
  optionalCallbackToWithdrawExistingRequest,
}: {
  section: Section
  createSectionInvoiceRequest: (data: {
    sectionRequestAssignmentCount: number
    sectionRequestStudentCount: number
  }) => Promise<void>
  onCreate?: () => void
  onSkip?: () => void
  optionalCallbackToWithdrawExistingRequest?: () => Promise<void>
}) {
  const useExpectedStudentFromSection = (section.data.expectedStudents ?? 0) > 0
  const { popDialog } = useDialogs()
  const repository = useRepository()
  const { t, tt } = useTranslationTyped()
  const headingId = useId()
  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      sectionRequestStudentCount: useExpectedStudentFromSection
        ? section.data.expectedStudents?.toString()
        : '',
      sectionRequestAssignmentCount: '',
    },
  })
  // undefined while the section's product is still being resolved, null once we
  // know there is no product attached
  const [sectionPassPriceCents, setSectionPassPriceCents] = useState<
    number | null | undefined
  >(undefined)

  useEffect(() => {
    let cancelled = false
    section.fetchSectionProductPriceCents().then((cents) => {
      if (!cancelled) setSectionPassPriceCents(cents)
    })
    return () => {
      cancelled = true
    }
  }, [section])

  const studentCount = watch('sectionRequestStudentCount')
  const assignmentCount = watch('sectionRequestAssignmentCount')

  const estimatedCostDollars = useMemo(() => {
    // don't show a number until we know which pricing model applies, otherwise
    // we'd flash a token estimate for a section that is priced per participant
    if (sectionPassPriceCents === undefined) return ''

    // token pricing reads the configured price, so wait for it to stream in
    // rather than briefly showing the default (BL-4756). Product pricing does
    // not read it. Mirrors the same guard in useInvoiceRequestEstimatedCost.
    if (
      !isInvoiceRequestProductPriced(sectionPassPriceCents) &&
      !repository.pricing.isLoaded
    ) {
      return ''
    }

    const { amount, maximumFractionDigits } =
      calculateInvoiceRequestEstimatedCost({
        studentCount: parseInt(studentCount) || 0,
        assignmentCount: parseInt(assignmentCount) || 0,
        tokenPrice: repository.pricing.tokenPriceInt,
        sectionPassPriceCents,
      })

    if (amount <= 0) return ''

    return formatCurrency(amount, 'USD', { maximumFractionDigits })
  }, [
    sectionPassPriceCents,
    studentCount,
    assignmentCount,
    repository.pricing.isLoaded,
    repository.pricing.tokenPriceInt,
  ])

  const onSubmit = async (data: FormValues) => {
    // We can safely cast data to FormValues because we know the form is valid.
    const values = data as FormValues

    // Parse the strings to numbers
    const sectionRequestStudentCount = parseInt(
      values.sectionRequestStudentCount
    )
    const sectionRequestAssignmentCount = parseInt(
      values.sectionRequestAssignmentCount
    )

    const [, err] = await noTryAsync(() =>
      createSectionInvoiceRequest({
        sectionRequestStudentCount,
        sectionRequestAssignmentCount,
      })
    )

    if (err) {
      console.error(err)
      toast.error(t('instructor_class.invoice_request_failed'))
      return
    }

    onCreate?.()
    popDialog()
  }

  return (
    <Dialog
      innerClassName="flex h-full"
      ignoreBackdropClick
      aria-labelledby={headingId}
    >
      <DialogCloseButton
        onClick={() => {
          onSkip?.()
          return true
        }}
      />
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex h-full flex-col justify-start"
      >
        <div className="mb-3 space-y-1">
          <h1 id={headingId} className="text-headline-large">
            {t('instructor_class.invoice_class')}
          </h1>
          <div className="text-body-medium text-on-surface-var">
            {t('instructor_class.invoice_class_description')}
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <Controller
            control={control}
            name="sectionRequestStudentCount"
            rules={{
              required: t('instructor_class.student_count_required'),
              min: {
                value: 1,
                message: t('instructor_class.student_count_min'),
              },
            }}
            render={({ field, fieldState }) => (
              <BreakoutTextInput
                type="number"
                kind="secondary"
                disabled={useExpectedStudentFromSection}
                error={fieldState.error}
                label={t(
                  'instructor_class.section_request_student_count_label'
                )}
                {...field}
                onChange={(e) => {
                  formatInt(e)
                  field.onChange(e)
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="sectionRequestAssignmentCount"
            rules={{
              required: t('instructor_class.assignment_count_required'),
              min: {
                value: 1,
                message: t('instructor_class.assignment_count_min'),
              },
            }}
            render={({ field, fieldState }) => (
              <BreakoutTextInput
                type="number"
                kind="secondary"
                error={fieldState.error}
                label={t(
                  'instructor_class.section_request_assignment_count_label'
                )}
                {...field}
                onChange={(e) => {
                  formatInt(e)
                  field.onChange(e)
                }}
              />
            )}
          />
          <strong
            className={classNames('px-3 text-body-medium', {
              invisible: estimatedCostDollars === '',
            })}
          >
            <Trans
              i18nKey="instructor_class.invoice_class_estimated_cost"
              values={{ dollars: estimatedCostDollars }}
              components={{ bold: <span className="font-bold" /> }}
            />
          </strong>
        </div>
        <div className="flex-1" />
        <div className="mt-2 space-y-3">
          <BreakoutButton
            size="large"
            type="submit"
            kind="primary"
            fullWidth
            // todo(ashold12): may want to allow submission on empty form
            disabled={isSubmitting || estimatedCostDollars === ''}
          >
            {tt.instructor_class.submit_invoice_request()}
          </BreakoutButton>

          {optionalCallbackToWithdrawExistingRequest ? (
            <BreakoutButton
              size="large"
              kind="secondary"
              fullWidth
              onClick={async () => {
                await optionalCallbackToWithdrawExistingRequest()
                popDialog()
                onSkip?.()
              }}
            >
              {tt.instructor_class.cancel_existing_request()}
            </BreakoutButton>
          ) : (
            <BreakoutButton
              size="large"
              kind="secondary"
              fullWidth
              onClick={() => {
                popDialog()
                onSkip?.()
              }}
            >
              {tt.instructor_class.dont_submit_invoice_request()}
            </BreakoutButton>
          )}
        </div>
      </form>
    </Dialog>
  )
})
